// Portail Client - Accès public via lien unique
// Devis, Signature électronique, Factures, Documents, Tickets

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Receipt, FolderOpen, MessageSquare, Download, CheckCircle2,
  Clock, Send, Eye, XCircle, AlertCircle, Pen, X, Building2, Phone, Mail,
  Calendar, Euro, ChevronRight, ExternalLink, Plus, Trash2, RotateCcw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast, Toaster } from 'sonner';

import { API_URL } from '../../config/api';

// Statuts devis
const DEVIS_STATUS = {
  sent: { label: 'En attente', color: 'bg-blue-100 text-blue-700', icon: Send },
  viewed: { label: 'Consulté', color: 'bg-indigo-100 text-indigo-700', icon: Eye },
  accepted: { label: 'Accepté', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Expiré', color: 'bg-orange-100 text-orange-700', icon: Clock },
  invoiced: { label: 'Facturé', color: 'bg-purple-100 text-purple-700', icon: Receipt },
};

// Statuts factures
const FACTURE_STATUS = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Envoyée', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700' },
  partial: { label: 'Paiement partiel', color: 'bg-yellow-100 text-yellow-700' },
  overdue: { label: 'En retard', color: 'bg-red-100 text-red-700' },
};

// ============ COMPOSANT SIGNATURE ============

function SignaturePad({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signedBy, setSignedBy] = useState('');
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Setup canvas
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSave = () => {
    if (!hasSignature) {
      toast.error('Dessine ta signature');
      return;
    }
    if (!signedBy.trim()) {
      toast.error('Entre ton nom');
      return;
    }
    if (!accepted) {
      toast.error('Veuillez accepter les conditions');
      return;
    }

    const canvas = canvasRef.current;
    const signatureImage = canvas.toDataURL('image/png');
    onSave({
      signature_image: signatureImage,
      signed_by: signedBy.trim(),
      acceptance_text: "J'accepte ce devis et ses conditions"
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-green-500 to-emerald-600">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Pen size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Signature électronique</h2>
              <p className="text-sm text-white/80">Signez pour accepter ce devis</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-white/20 rounded-lg text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Nom du signataire */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Votre nom complet *
            </label>
            <Input
              value={signedBy}
              onChange={(e) => setSignedBy(e.target.value)}
              placeholder="Jean Dupont"
              className="rounded-xl"
            />
          </div>

          {/* Zone de signature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Votre signature *
            </label>
            <div className="relative border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50">
              <canvas
                ref={canvasRef}
                width={450}
                height={200}
                className="w-full touch-none cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              {!hasSignature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-gray-400 text-sm">Dessine ta signature ici</p>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={clearSignature}
              className="mt-2 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <RotateCcw size={14} /> Effacer
            </button>
          </div>

          {/* Checkbox acceptation */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm text-gray-600">
              J'accepte ce devis et ses conditions. Je comprends que cette signature électronique 
              a la même valeur juridique qu'une signature manuscrite.
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onCancel} className="rounded-xl">
            Annuler
          </Button>
          <Button 
            onClick={handleSave}
            className="bg-green-500 hover:bg-green-600 rounded-xl"
          >
            <CheckCircle2 size={18} className="mr-2" />
            Signer et accepter
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ COMPOSANT DÉTAIL DEVIS ============

function DevisDetail({ devis, onClose, onSign, onDownload }) {
  const status = DEVIS_STATUS[devis.status] || DEVIS_STATUS.sent;
  const StatusIcon = status.icon;
  const canSign = ['sent', 'viewed'].includes(devis.status);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { 
        day: '2-digit', month: 'long', year: 'numeric' 
      });
    } catch { return dateStr; }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center">
              <FileText size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{devis.devis_number}</h2>
              <p className="text-sm text-gray-500">{devis.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${status.color}`}>
              <StatusIcon size={14} />
              {status.label}
            </span>
            <button onClick={onClose} className="p-2 hover:bg-orange-100 rounded-lg">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-6">
          {/* Info */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-500 mb-1">Date de création</p>
              <p className="font-medium">{formatDate(devis.created_at)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-500 mb-1">Validité</p>
              <p className="font-medium">{formatDate(devis.valid_until)}</p>
            </div>
          </div>

          {/* Lignes du devis */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Détail</h3>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600">Description</th>
                    <th className="text-center p-3 font-medium text-gray-600">Qté</th>
                    <th className="text-right p-3 font-medium text-gray-600">Prix unit.</th>
                    <th className="text-right p-3 font-medium text-gray-600">Total HT</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {devis.lines?.map((line, index) => (
                    <tr key={index}>
                      <td className="p-3">
                        <p className="font-medium text-gray-900">{line.description}</p>
                        {line.details && <p className="text-gray-500 text-xs mt-1">{line.details}</p>}
                      </td>
                      <td className="p-3 text-center text-gray-600">
                        {line.quantity} {line.unit}
                      </td>
                      <td className="p-3 text-right text-gray-600">
                        {line.unit_price?.toFixed(2)} €
                      </td>
                      <td className="p-3 text-right font-medium text-gray-900">
                        {line.subtotal?.toFixed(2)} €
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totaux */}
          <div className="bg-orange-50 rounded-xl p-4 mb-6">
            <div className="flex flex-col items-end space-y-2">
              <div className="flex items-center justify-between w-64">
                <span className="text-gray-600">Total HT</span>
                <span className="font-medium">{devis.subtotal_ht?.toFixed(2)} €</span>
              </div>
              <div className="flex items-center justify-between w-64">
                <span className="text-gray-600">TVA</span>
                <span className="font-medium">{devis.total_tva?.toFixed(2)} €</span>
              </div>
              {devis.global_discount_percent > 0 && (
                <div className="flex items-center justify-between w-64 text-green-600">
                  <span>Remise ({devis.global_discount_percent}%)</span>
                  <span>-{devis.global_discount_amount?.toFixed(2)} €</span>
                </div>
              )}
              <div className="flex items-center justify-between w-64 border-t border-orange-200 pt-2">
                <span className="font-bold text-gray-900">Total TTC</span>
                <span className="text-xl font-bold text-orange-600">{devis.total_ttc?.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {devis.notes && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
              <p className="text-gray-600 text-sm whitespace-pre-line">{devis.notes}</p>
            </div>
          )}

          {/* Signature si déjà signé */}
          {devis.signed && devis.signature_image && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={20} className="text-green-600" />
                <span className="font-semibold text-green-800">Devis accepté et signé</span>
              </div>
              <div className="bg-white rounded-lg p-3 inline-block">
                <img 
                  src={devis.signature_image} 
                  alt="Signature" 
                  className="max-h-24"
                />
              </div>
              <p className="text-sm text-green-700 mt-2">
                Signé par {devis.signed_by} le {formatDate(devis.signed_at)}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={() => onDownload(devis)} className="rounded-xl">
            <Download size={18} className="mr-2" />
            Télécharger PDF
          </Button>
          
          {canSign && (
            <Button 
              onClick={() => onSign(devis)}
              className="bg-green-500 hover:bg-green-600 rounded-xl"
            >
              <Pen size={18} className="mr-2" />
              Signer ce devis
            </Button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ COMPOSANT NOUVEAU TICKET ============

function NewTicketForm({ onSubmit, onCancel }) {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !content.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setSubmitting(true);
    await onSubmit({ subject, content, category });
    setSubmitting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-blue-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
              <MessageSquare size={20} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Nouveau ticket</h2>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-blue-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sujet *</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex: Question sur le devis"
              className="rounded-xl"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl"
            >
              <option value="general">Question générale</option>
              <option value="devis">Devis</option>
              <option value="facture">Facturation</option>
              <option value="technique">Technique</option>
              <option value="autre">Autre</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Décris ta demande..."
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none"
              required
            />
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onCancel} className="rounded-xl">
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-blue-500 hover:bg-blue-600 rounded-xl"
          >
            {submitting ? 'Envoi...' : 'Envoyer'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ PAGE PRINCIPALE ============

export default function ClientPortalDevisPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('devis');
  
  const [portalInfo, setPortalInfo] = useState(null);
  const [summary, setSummary] = useState(null);
  const [devisList, setDevisList] = useState([]);
  const [facturesList, setFacturesList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tickets, setTickets] = useState([]);
  
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [signingDevis, setSigningDevis] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);

  // Charger les données
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Info portail
      const infoRes = await fetch(`${API_URL}/api/portal/${token}`);
      if (!infoRes.ok) {
        if (infoRes.status === 404) {
          setError('Ce lien est invalide ou a expiré');
          return;
        }
        throw new Error('Erreur de chargement');
      }
      const info = await infoRes.json();
      setPortalInfo(info);

      // Résumé
      const summaryRes = await fetch(`${API_URL}/api/portal/${token}/summary`);
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }

      // Devis
      const devisRes = await fetch(`${API_URL}/api/portal/${token}/devis`);
      if (devisRes.ok) {
        setDevisList(await devisRes.json());
      }

      // Factures
      const facturesRes = await fetch(`${API_URL}/api/portal/${token}/factures`);
      if (facturesRes.ok) {
        setFacturesList(await facturesRes.json());
      }

      // Documents
      const docsRes = await fetch(`${API_URL}/api/portal/${token}/documents`);
      if (docsRes.ok) {
        setDocuments(await docsRes.json());
      }

      // Tickets
      const ticketsRes = await fetch(`${API_URL}/api/portal/${token}/tickets`);
      if (ticketsRes.ok) {
        setTickets(await ticketsRes.json());
      }

    } catch (e) {
      console.error('Error:', e);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);

  // Télécharger PDF
  const handleDownloadPDF = (devis) => {
    window.open(`${API_URL}/api/portal/${token}/devis/${devis.id}/pdf`, '_blank');
  };

  const handleDownloadFacturePDF = (facture) => {
    window.open(`${API_URL}/api/portal/${token}/factures/${facture.id}/pdf`, '_blank');
  };

  // Signer devis
  const handleSign = async (signatureData) => {
    try {
      const res = await fetch(`${API_URL}/api/portal/${token}/devis/${signingDevis.id}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signatureData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur lors de la signature');
      }

      toast.success('Devis signé avec succès !');
      setShowSignature(false);
      setSigningDevis(null);
      setSelectedDevis(null);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Créer ticket
  const handleCreateTicket = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/portal/${token}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!res.ok) throw new Error('Erreur lors de la création');

      toast.success('Ticket créé avec succès');
      setShowNewTicket(false);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  // Erreur
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-xl">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
          <p className="text-gray-500">Chargement de ton espace...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'devis', label: 'Devis', icon: FileText, count: summary?.devis?.total || 0, badge: summary?.devis?.pending_signature },
    { id: 'factures', label: 'Factures', icon: Receipt, count: summary?.factures?.total || 0, badge: summary?.factures?.unpaid },
    { id: 'documents', label: 'Documents', icon: FolderOpen, count: summary?.documents?.total || 0 },
    { id: 'tickets', label: 'Support', icon: MessageSquare, count: tickets.length, badge: summary?.tickets?.open },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {portalInfo?.seller?.logo ? (
                <img 
                  src={portalInfo.seller.logo} 
                  alt={portalInfo.seller.name}
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Building2 size={24} className="text-orange-600" />
                </div>
              )}
              <div>
                <h1 className="font-bold text-gray-900">{portalInfo?.seller?.name}</h1>
                <p className="text-sm text-gray-500">Espace client de {portalInfo?.client?.name}</p>
              </div>
            </div>
            
            <div className="hidden sm:flex items-center gap-4 text-sm text-gray-500">
              {portalInfo?.seller?.phone && (
                <a href={`tel:${portalInfo.seller.phone}`} className="flex items-center gap-1 hover:text-gray-700">
                  <Phone size={14} /> {portalInfo.seller.phone}
                </a>
              )}
              {portalInfo?.seller?.email && (
                <a href={`mailto:${portalInfo.seller.email}`} className="flex items-center gap-1 hover:text-gray-700">
                  <Mail size={14} /> {portalInfo.seller.email}
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-[73px] z-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex overflow-x-auto hide-scrollbar">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    isActive 
                      ? 'border-orange-500 text-orange-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon size={18} />
                  <span className="font-medium">{tab.label}</span>
                  {tab.badge > 0 && (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* DEVIS */}
        {activeTab === 'devis' && (
          <div className="space-y-4">
            {devisList.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Aucun devis</h3>
                <p className="text-gray-500">Vous n'avez pas encore de devis</p>
              </div>
            ) : (
              devisList.map((devis) => {
                const status = DEVIS_STATUS[devis.status] || DEVIS_STATUS.sent;
                const StatusIcon = status.icon;
                return (
                  <motion.div
                    key={devis.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => setSelectedDevis(devis)}
                  >
                    <div className="p-4 sm:p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileText size={24} className="text-orange-600" />
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{devis.devis_number}</p>
                            <h3 className="text-lg font-semibold text-gray-800 mt-1">{devis.title}</h3>
                            <p className="text-sm text-gray-500 mt-1">
                              Créé le {formatDate(devis.created_at)} • Valide jusqu'au {formatDate(devis.valid_until)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                            <StatusIcon size={14} />
                            {status.label}
                          </span>
                          <p className="text-2xl font-bold text-gray-900 mt-2">{devis.total_ttc?.toFixed(2)} €</p>
                        </div>
                      </div>
                      
                      {['sent', 'viewed'].includes(devis.status) && (
                        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                          <p className="text-sm text-orange-600 font-medium">
                            ✍️ En attente de ta signature
                          </p>
                          <Button 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSigningDevis(devis);
                              setShowSignature(true);
                            }}
                            className="bg-green-500 hover:bg-green-600 rounded-lg"
                          >
                            <Pen size={14} className="mr-1" /> Signer
                          </Button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        )}

        {/* FACTURES */}
        {activeTab === 'factures' && (
          <div className="space-y-4">
            {facturesList.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <Receipt size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Aucune facture</h3>
                <p className="text-gray-500">Vous n'avez pas encore de factures</p>
              </div>
            ) : (
              facturesList.map((facture) => {
                const status = FACTURE_STATUS[facture.status] || FACTURE_STATUS.sent;
                return (
                  <motion.div
                    key={facture.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Receipt size={24} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{facture.facture_number}</p>
                          {facture.devis_number && (
                            <p className="text-sm text-gray-500">Devis: {facture.devis_number}</p>
                          )}
                          <p className="text-sm text-gray-500 mt-1">
                            Émise le {formatDate(facture.created_at)}
                            {facture.due_date && ` • Échéance: ${formatDate(facture.due_date)}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                          {status.label}
                        </span>
                        <p className="text-2xl font-bold text-gray-900 mt-2">{facture.total_ttc?.toFixed(2)} €</p>
                        {facture.amount_due > 0 && facture.status !== 'paid' && (
                          <p className="text-sm text-red-500">Reste dû: {facture.amount_due?.toFixed(2)} €</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDownloadFacturePDF(facture)}
                        className="rounded-lg"
                      >
                        <Download size={14} className="mr-1" /> Télécharger PDF
                      </Button>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        )}

        {/* DOCUMENTS */}
        {activeTab === 'documents' && (
          <div className="space-y-4">
            {documents.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <FolderOpen size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Aucun document</h3>
                <p className="text-gray-500">Pas de documents partagés pour le moment</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                {documents.map((doc) => (
                  <div key={doc.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FolderOpen size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{doc.name}</p>
                        <p className="text-sm text-gray-500">{formatDate(doc.uploaded_at)}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="rounded-lg">
                      <Download size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TICKETS */}
        {activeTab === 'tickets' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button 
                onClick={() => setShowNewTicket(true)}
                className="bg-blue-500 hover:bg-blue-600 rounded-xl"
              >
                <Plus size={18} className="mr-2" /> Nouveau ticket
              </Button>
            </div>

            {tickets.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Aucun ticket</h3>
                <p className="text-gray-500 mb-4">Vous pouvez créer un ticket pour contacter le support</p>
              </div>
            ) : (
              tickets.map((ticket) => (
                <motion.div
                  key={ticket.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-gray-500">{ticket.ticket_number}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          ticket.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                          ticket.status === 'open' ? 'bg-green-100 text-green-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {ticket.status === 'closed' ? 'Fermé' : 
                           ticket.status === 'open' ? 'Ouvert' : 'En attente'}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 mt-1">{ticket.subject}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Créé le {formatDate(ticket.created_at)}
                        {ticket.messages?.length > 1 && ` • ${ticket.messages.length} messages`}
                      </p>
                    </div>
                    <ChevronRight size={20} className="text-gray-400" />
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <AnimatePresence>
        {selectedDevis && !showSignature && (
          <DevisDetail
            devis={selectedDevis}
            onClose={() => setSelectedDevis(null)}
            onSign={(devis) => {
              setSigningDevis(devis);
              setShowSignature(true);
            }}
            onDownload={handleDownloadPDF}
          />
        )}
        
        {showSignature && signingDevis && (
          <SignaturePad
            onSave={handleSign}
            onCancel={() => {
              setShowSignature(false);
              setSigningDevis(null);
            }}
          />
        )}

        {showNewTicket && (
          <NewTicketForm
            onSubmit={handleCreateTicket}
            onCancel={() => setShowNewTicket(false)}
          />
        )}
      </AnimatePresence>

      {/* CSS pour cacher la scrollbar */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
